import * as React from "react";
import { X } from "lucide-react";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Module-level dismiss suppression. When any Coachmark's primary action
// fires (typically advancing to the next step), the next coachmark's
// freshly-mounted Popover can immediately receive the trailing pointerup
// as onInteractOutside and close itself. Arming this window from
// handlePrimary suppresses that spurious dismiss across coachmark
// instances during the transition.
let suppressCoachmarkDismissUntil = 0;

export interface CoachmarkProps {
  children: React.ReactNode;
  open: boolean;
  title: string;
  body: string;
  step?: { current: number; total: number };
  primaryLabel?: string;
  onPrimary: () => void;
  onDismiss?: () => void;
}

export function Coachmark({
  children,
  open,
  title,
  body,
  step,
  primaryLabel = "Got it",
  onPrimary,
  onDismiss,
}: CoachmarkProps) {
  // Two issues this component absorbs so callers don't have to:
  //
  // 1) Synchronous teardown inside a Radix click handler causes
  //    "Maximum update depth exceeded" via DismissableLayer's setRef
  //    bookkeeping. Defer onPrimary/onDismiss to the next tick so the
  //    popover finishes processing the pointer event before state moves.
  // 2) When `open` flips true, the trailing pointerup from the click that
  //    advanced the step can fire onInteractOutside immediately and close
  //    the freshly-mounted coachmark. Arm a short grace window after each
  //    open transition that swallows dismiss attempts.
  const ignoreDismissUntilRef = React.useRef<number>(0);
  React.useEffect(() => {
    if (open) ignoreDismissUntilRef.current = Date.now() + 350;
  }, [open]);

  const handlePrimary = React.useCallback(() => {
    suppressCoachmarkDismissUntil = Date.now() + 500;
    setTimeout(() => onPrimary(), 0);
  }, [onPrimary]);

  const handleDismiss = React.useCallback(() => {
    if (!onDismiss) return;
    if (Date.now() < ignoreDismissUntilRef.current) return;
    if (Date.now() < suppressCoachmarkDismissUntil) return;
    setTimeout(() => onDismiss(), 0);
  }, [onDismiss]);

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        sideOffset={8}
        className={cn(
          "w-80 border-brand-line bg-brand-paper p-0 text-brand-ink shadow-lg",
        )}
        onInteractOutside={(e) => {
          if (onDismiss) {
            e.preventDefault();
            handleDismiss();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (onDismiss) {
            e.preventDefault();
            handleDismiss();
          }
        }}
      >
        <div className="flex items-start justify-between gap-2 px-4 pt-4">
          <h3 className="font-serif text-base font-semibold leading-snug text-brand-ink">
            {title}
          </h3>
          {onDismiss && (
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-brand-muted transition-colors hover:text-brand-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-ink/30"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <div className="px-4 pb-4 pt-2">
          <p className="text-sm leading-relaxed text-brand-ink/90">{body}</p>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-brand-line px-4 py-3">
          {step ? (
            <span className="text-xs font-medium tabular-nums text-brand-accent">
              {step.current} of {step.total}
            </span>
          ) : (
            <span aria-hidden />
          )}
          <Button size="sm" onClick={handlePrimary}>
            {primaryLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
